import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS, markLanguageExplicit } from '../i18n'
import { useTheme } from '../providers/ThemeProvider'
import { useAuthStore } from '../stores/authStore'
import { deactivateDemo } from '../services/demoMode'
import { useAccount } from '../features/settings/useAccount'
import { useAccountMutations } from '../features/settings/useAccountMutations'
import { AccountInfoSection } from '../features/settings/AccountInfoSection'
import { PasswordChangeForm } from '../features/settings/PasswordChangeForm'
import { SoundVolumeSection } from '../features/settings/SoundVolumeSection'
import { ApiKeySection } from '../features/settings/ApiKeySection'
import { AlertMatrixCard } from '../features/notifications/AlertMatrixCard'
import { TrafficThresholdCard } from '../features/notifications/TrafficThresholdCard'
import { GuardSettingsSection } from '../features/settings/GuardSettingsSection'
import { TelegramSection } from '../features/settings/TelegramSection'
import { FcmPermissionBanner } from '../features/settings/FcmPermissionBanner'
import { SecuritySection } from '../features/settings/SecuritySection'
import { AnalyticsConsentSection } from '../features/settings/AnalyticsConsentSection'
import { toast } from '../components/Toast/useToast'

export const Route = createFileRoute('/_auth/settings')({
  component: SettingsPage,
})

// ---------------------------------------------------------------------------
// Display-Settings-Form
// ---------------------------------------------------------------------------

const CURRENCIES = ['EUR', 'USD', 'CHF', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'BTC', 'ETH', 'HNT']
/** IANA-Zeitzonen aus der Laufzeit (native `Intl`-API statt eigener Liste); Fallback fuer aeltere Engines. */
function listTimeZones(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone')
  } catch {
    return ['UTC', 'Europe/Berlin', 'Europe/London', 'America/New_York', 'America/Los_Angeles', 'Asia/Tokyo']
  }
}
const TIMEZONES = listTimeZones()
const DATE_FORMATS = [
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
]
const NUM_DIGITS_OPTIONS = [1, 2, 3, 4, 5, 6]

type DisplaySectionProps = {
  theme: string
  use_metric: boolean
  time_format: string
  date_format: string
  num_digits: number
  newsletter: boolean
  monthly_report: boolean
  isMutating: boolean
  onSave: (values: {
    theme: string
    use_metric: boolean
    time_format: string
    date_format: string
    num_digits: number
    newsletter: boolean
    monthly_report: boolean
  }) => void
}

function DisplaySection({
  theme,
  use_metric,
  time_format,
  date_format,
  num_digits,
  newsletter,
  monthly_report,
  isMutating,
  onSave,
}: DisplaySectionProps) {
  const { setTheme } = useTheme()
  const [localTheme, setLocalTheme] = useState(theme)
  // Sprache wird direkt über i18n + localStorage gesteuert (kein Account-Feld).
  const { t: tc, i18n } = useTranslation('common')
  const { t } = useTranslation('settings')
  const [localMetric, setLocalMetric] = useState(use_metric)
  const [localTimeFormat, setLocalTimeFormat] = useState(time_format)
  const [localDateFormat, setLocalDateFormat] = useState(date_format)
  const [localNumDigits, setLocalNumDigits] = useState(num_digits)
  const [localNewsletter, setLocalNewsletter] = useState(newsletter)
  const [localMonthlyReport, setLocalMonthlyReport] = useState(monthly_report)

  const THEMES = [
    { value: 'light', label: t('display.themes.light') },
    { value: 'dark', label: t('display.themes.dark') },
    { value: 'system', label: t('display.themes.system') },
  ]
  const TIME_FORMATS = [
    { value: 'HH:mm', label: '24h — 14:30' },
    { value: 'hh:mm a', label: '12h — 2:30 PM' },
    { value: 'H:mm', label: t('display.timeFormats.compact') },
  ]

  function handleSave() {
    if (localTheme === 'light' || localTheme === 'dark' || localTheme === 'system') {
      setTheme(localTheme)
    }
    onSave({
      theme: localTheme,
      use_metric: localMetric,
      time_format: localTimeFormat,
      date_format: localDateFormat,
      num_digits: localNumDigits,
      newsletter: localNewsletter,
      monthly_report: localMonthlyReport,
    })
  }

  return (
    <div className="card card-pad" style={{ marginBottom: 18 }}>
      <h3 style={{ marginBottom: 14 }}>{t('display.title')}</h3>
      <div className="grid grid-2" style={{ marginBottom: 14 }}>
        <div>
          <label className="label" htmlFor="theme">
            {t('display.colorScheme')}
          </label>
          <select
            id="theme"
            className="input"
            value={localTheme}
            onChange={(e) => setLocalTheme(e.target.value)}
            data-testid="theme-select"
          >
            {THEMES.map((th) => (
              <option key={th.value} value={th.value}>
                {th.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="language">
            {tc('language.label')}
          </label>
          <select
            id="language"
            className="input"
            value={i18n.resolvedLanguage ?? i18n.language}
            onChange={(e) => {
              markLanguageExplicit()
              i18n.changeLanguage(e.target.value)
            }}
            data-testid="language-select"
          >
            {SUPPORTED_LANGUAGES.map((lng) => (
              <option key={lng} value={lng}>
                {LANGUAGE_LABELS[lng]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="time_format">
            {t('display.timeFormat')}
          </label>
          <select
            id="time_format"
            className="input"
            value={localTimeFormat}
            onChange={(e) => setLocalTimeFormat(e.target.value)}
          >
            {TIME_FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="date_format">
            {t('display.dateFormat')}
          </label>
          <select
            id="date_format"
            className="input"
            value={localDateFormat}
            onChange={(e) => setLocalDateFormat(e.target.value)}
          >
            {DATE_FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="num_digits">
            {t('display.numDigits')}
          </label>
          <select
            id="num_digits"
            className="input"
            value={localNumDigits}
            onChange={(e) => setLocalNumDigits(Number(e.target.value))}
          >
            {NUM_DIGITS_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <label htmlFor="use_metric" style={{ fontSize: 13, cursor: 'pointer' }}>
            {t('display.useMetric')}
          </label>
          <span
            id="use_metric"
            role="switch"
            aria-checked={localMetric}
            tabIndex={0}
            className={`switch${localMetric ? ' is-on' : ''}`}
            onClick={() => setLocalMetric((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setLocalMetric((v) => !v)
              }
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <label htmlFor="newsletter" style={{ fontSize: 13, cursor: 'pointer' }}>
            {t('display.newsletter')}
          </label>
          <span
            id="newsletter"
            role="switch"
            aria-checked={localNewsletter}
            tabIndex={0}
            className={`switch${localNewsletter ? ' is-on' : ''}`}
            onClick={() => setLocalNewsletter((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setLocalNewsletter((v) => !v)
              }
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <label htmlFor="monthly_report" style={{ fontSize: 13, cursor: 'pointer' }}>
            {t('display.monthlyReport')}
          </label>
          <span
            id="monthly_report"
            role="switch"
            aria-checked={localMonthlyReport}
            tabIndex={0}
            className={`switch${localMonthlyReport ? ' is-on' : ''}`}
            onClick={() => setLocalMonthlyReport((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setLocalMonthlyReport((v) => !v)
              }
            }}
          />
        </div>
      </div>
      <button type="button" className="btn btn-sm btn-primary" onClick={handleSave} disabled={isMutating}>
        {tc('actions.save')}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Account-Karte — Design-Referenz settings.html Profil-Tab, linke Spalte:
// EIN Account-Formular (Name + E-Mail readonly + Waehrung + Zeitzone
// editierbar + Save). Waehrung/Zeitzone sind die einzigen echten, ueber
// PATCH account/me persistierbaren Profilfelder; ein "Name"-Feld existiert im
// Backend-Contract nicht (weder im Account-Response- noch im Update-Schema,
// siehe rails_backend_v2/swagger/v2/swagger.yaml) — statt eines funktionslosen
// Eingabefelds zeigen wir daher nur einen kurzen, nutzerverstaendlichen Hinweis
// (siehe #111: keine Implementierungsdetails im UI-Text).
// ---------------------------------------------------------------------------

type AccountFormSectionProps = {
  email: string
  currency: string
  time_zone: string
  isMutating: boolean
  onSave: (values: { currency: string; time_zone: string }) => void
}

function AccountFormSection({ email, currency, time_zone, isMutating, onSave }: AccountFormSectionProps) {
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')
  const [localCurrency, setLocalCurrency] = useState(currency)
  const [localTimezone, setLocalTimezone] = useState(time_zone)

  return (
    <div className="card card-pad" style={{ marginBottom: 18 }} data-testid="profile-edit-section">
      <h3 style={{ marginBottom: 14 }}>{t('account.title')}</h3>
      <div className="grid grid-2" style={{ marginBottom: 14 }}>
        <div>
          <span className="label">{t('account.name')}</span>
          <div className="muted" style={{ fontSize: 13 }} data-testid="profile-name-hint">
            {t('account.nameUnsupportedHint')}
          </div>
        </div>
        <div>
          <label className="label">{t('account.email')}</label>
          <div className="input" style={{ cursor: 'default', userSelect: 'text' }}>
            {email}
          </div>
        </div>
        <div>
          <label className="label" htmlFor="profile-currency">
            {t('display.currency')}
          </label>
          <select
            id="profile-currency"
            className="input"
            value={localCurrency}
            onChange={(e) => setLocalCurrency(e.target.value)}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="profile-timezone">
            {t('display.timezone')}
          </label>
          <select
            id="profile-timezone"
            className="input"
            value={localTimezone}
            onChange={(e) => setLocalTimezone(e.target.value)}
          >
            {/* Aktueller Wert bleibt waehlbar, auch wenn er nicht in der Intl-Liste steht. */}
            {!TIMEZONES.includes(localTimezone) && localTimezone && (
              <option value={localTimezone}>{localTimezone}</option>
            )}
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>
      </div>
      <button
        type="button"
        className="btn btn-sm btn-primary"
        onClick={() => onSave({ currency: localCurrency, time_zone: localTimezone })}
        disabled={isMutating}
      >
        {tc('actions.save')}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Hauptseite
// ---------------------------------------------------------------------------

type SettingsTab = 'profile' | 'notifications' | 'display' | 'security'
const SETTINGS_TABS: SettingsTab[] = ['profile', 'notifications', 'display', 'security']

function SettingsPage() {
  const { account, isLoading, isError } = useAccount()
  const { isMutating, updateSettings, changePassword } = useAccountMutations()
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const navigate = useNavigate()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')
  const { t } = useTranslation('settings')
  const { t: tc } = useTranslation('common')

  function handleLogout() {
    deactivateDemo()
    clearAuth()
    navigate({ to: '/login', replace: true })
  }

  async function handleDisplaySave(values: {
    theme: string
    use_metric: boolean
    time_format: string
    date_format: string
    num_digits: number
    newsletter: boolean
    monthly_report: boolean
  }) {
    const result = await updateSettings(values)
    if (result.ok) {
      toast.success(t('toasts.displaySaved'))
    } else {
      toast.error(result.message ?? t('toasts.displayError'))
    }
  }

  async function handleProfileSave(values: { currency: string; time_zone: string }) {
    const result = await updateSettings(values)
    if (result.ok) {
      toast.success(t('toasts.displaySaved'))
    } else {
      toast.error(result.message ?? t('toasts.displayError'))
    }
  }

  async function handlePasswordSuccess() {
    toast.success(t('password.successToast'))
  }

  if (isLoading) {
    return (
      <div style={{ maxWidth: 860 }}>
        <div className="section-header">
          <h1>{t('page.title')}</h1>
        </div>
        <div className="card card-pad">
          <div className="muted" style={{ fontSize: 13 }}>
            {t('page.loading')}
          </div>
        </div>
      </div>
    )
  }

  if (isError || !account) {
    return (
      <div style={{ maxWidth: 860 }}>
        <div className="section-header">
          <h1>{t('page.title')}</h1>
        </div>
        <div className="card card-pad">
          <div className="muted" style={{ fontSize: 13, color: 'var(--danger)' }}>
            {t('page.loadError')}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 860 }}>
      <div className="section-header">
        <div>
          <h1>{t('page.title')}</h1>
          <p>{t('page.subtitle')}</p>
        </div>
      </div>

      {/* Tab-Navigation — Design-Referenz settings.html: horizontale Tabs
          statt einer langen einspaltigen Kartenkolonne. */}
      <div className="tabs" role="tablist" aria-label={t('tabs.ariaLabel')} style={{ marginBottom: 20 }}>
        {SETTINGS_TABS.map((tab) => (
          <div
            key={tab}
            role="tab"
            tabIndex={0}
            aria-selected={activeTab === tab}
            className={`tab${activeTab === tab ? ' is-active' : ''}`}
            onClick={() => setActiveTab(tab)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setActiveTab(tab)
            }}
            data-testid={`settings-tab-${tab}`}
          >
            {t(`tabs.${tab}`)}
          </div>
        ))}
      </div>

      {/* Profil — Design-Referenz settings.html: EINE Account-Karte
          (E-Mail/Waehrung/Zeitzone/Save) links + Account-Zusammenfassung
          (Plan/Ablauf/Kontingente/Abo-Link) rechts. */}
      <section hidden={activeTab !== 'profile'} className="settings-profile-grid">
        <div style={{ minWidth: 0 }}>
          {/* Account-Formular — E-Mail (readonly) + Waehrung/Zeitzone (editierbar+speicherbar) */}
          <AccountFormSection
            email={account.email}
            currency={account.currency}
            time_zone={account.time_zone}
            isMutating={isMutating}
            onSave={handleProfileSave}
          />
        </div>

        <div style={{ minWidth: 0 }}>
          {/* Account-Zusammenfassung — N2: Ablauf-Kennzeichnung in AccountInfoSection */}
          <AccountInfoSection account={account} />
        </div>
      </section>
      <style>{`
        .settings-profile-grid[hidden] { display: none; }
        .settings-profile-grid { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 20px; align-items: start; }
        @media (max-width: 900px) {
          .settings-profile-grid { grid-template-columns: minmax(0, 1fr); }
        }
      `}</style>

      {/* Darstellung — eigener Tab (Design-Referenz settings.html: "Display"). */}
      <section hidden={activeTab !== 'display'}>
        <DisplaySection
          theme={account.theme}
          use_metric={account.use_metric}
          time_format={account.time_format}
          date_format={account.date_format}
          num_digits={account.num_digits}
          newsletter={account.newsletter}
          monthly_report={account.monthly_report}
          isMutating={isMutating}
          onSave={handleDisplaySave}
        />
      </section>

      {/* Sicherheit — eigener Tab (Design-Referenz settings.html: Passwort links,
          2FA/Sessions rechts; API-Schluessel + Gefahrenbereich darunter). */}
      <section hidden={activeTab !== 'security'} className="settings-security-grid">
        <div style={{ minWidth: 0 }}>
          {/* Passwort-Aenderung */}
          <div className="card card-pad" style={{ marginBottom: 18 }} data-testid="password-section">
            <h3 style={{ marginBottom: 14 }}>{t('password.title')}</h3>
            <PasswordChangeForm onSuccess={handlePasswordSuccess} changePassword={changePassword} isMutating={isMutating} />
          </div>

          {/* API-Schluessel */}
          <ApiKeySection
            apiKey={account.api_key}
            apiKeyMasked={account.api_key_masked}
            apiAccess={account.api_access}
          />

          {/* Gefahrenbereich */}
          <div
            className="card card-pad"
            style={{ borderColor: 'color-mix(in oklch, var(--danger) 30%, var(--border))' }}
            data-testid="danger-zone"
          >
            <h3 style={{ marginBottom: 14, color: 'var(--danger)' }}>{t('dangerZone.title')}</h3>
            <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
              {t('dangerZone.description')}
            </p>
            {showDeleteConfirm ? (
              <div>
                <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--danger)', marginBottom: 12 }}>
                  {t('dangerZone.confirm')}
                </p>
                <div className="row" style={{ gap: 8 }}>
                  <button type="button" onClick={() => setShowDeleteConfirm(false)} className="btn btn-sm">
                    {tc('actions.cancel')}
                  </button>
                  <a
                    href={`mailto:support@heliumtracker.io?subject=${encodeURIComponent(t('dangerZone.deletionEmailSubject'))}`}
                    className="btn btn-sm btn-danger"
                  >
                    {t('dangerZone.requestDeletion')}
                  </a>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setShowDeleteConfirm(true)} className="btn btn-sm btn-danger">
                {t('dangerZone.deleteAccount')}
              </button>
            )}
          </div>
        </div>

        {/* 2FA und Active-Sessions (demnächst), rechte Spalte wie in der
            Vorlage (settings.html: 1fr 320px). */}
        <div style={{ minWidth: 0, display: 'grid', gap: 20 }}>
          <SecuritySection onLogout={handleLogout} />
          {/* Widerruf der Analytics-Einwilligung — muss genauso erreichbar
              sein wie die Zustimmung im Banner. */}
          <AnalyticsConsentSection />
        </div>
      </section>
      <style>{`
        /* [hidden]{display:none} ist eine UA-Default-Regel OHNE !important —
           jede Autoren-Regel mit display gewinnt im Cascade dagegen (Bug web#27:
           Tab-Wechsel aktualisierte activeTab korrekt, aber das Profil-Panel
           blieb dank dieser Klasse trotz hidden=true sichtbar). [hidden]
           explizit vorrangig behandeln. */
        .settings-security-grid[hidden] { display: none; }
        .settings-security-grid { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 20px; align-items: start; }
        @media (max-width: 900px) {
          .settings-security-grid { grid-template-columns: minmax(0, 1fr); }
        }
      `}</style>

      {/* Benachrichtigungen — Story 3.4: kanonische Alert-Matrix (19 Events x
          4 Kanaele) + Telegram-Connect + Traffic-Schwellenwert, Layout gemaess
          Design-Referenz settings.html (Notifications-Tab: Matrix links,
          Telegram/Schwellenwert rechts, einspaltig unter 900px). */}
      <section hidden={activeTab !== 'notifications'}>
        <div className="notifications-settings-grid" data-testid="notifications-settings">
          <AlertMatrixCard />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
            <TelegramSection />
            <TrafficThresholdCard />
          </div>
        </div>

        {/* Sound-Lautstaerke */}
        <SoundVolumeSection
          initialVolume={account.volume ?? 0.5}
          isMutating={isMutating}
          onSave={async (volume) => {
            const result = await updateSettings({ volume })
            if (result.ok) {
              toast.success(t('toasts.volumeSaved'))
            } else {
              toast.error(result.message ?? t('toasts.volumeError'))
            }
          }}
        />

        {/* Guard-Einstellungen */}
        <div className="card card-pad" style={{ marginBottom: 18 }} data-testid="guard-settings-section">
          <GuardSettingsSection
            account={account}
            isMutating={isMutating}
            onSave={async (payload) => {
              const result = await updateSettings(payload as Parameters<typeof updateSettings>[0])
              if (result.ok) {
                toast.success(t('toasts.guardSaved'))
              } else {
                toast.error(result.message ?? t('toasts.guardError'))
              }
            }}
          />
        </div>

        {/* FCM Push-Permission-Banner */}
        <FcmPermissionBanner
          isMutating={isMutating}
          onSave={async (payload) => {
            const result = await updateSettings(payload as Parameters<typeof updateSettings>[0])
            if (result.ok) {
              toast.success(t('fcm.pushActivated'))
            } else {
              toast.error(result.message ?? t('fcm.pushActivateError'))
            }
          }}
        />
      </section>
      <style>{`
        .notifications-settings-grid { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 18px; align-items: start; margin-bottom: 18px; }
        .notifications-settings-grid > * { min-width: 0; }
        @media (max-width: 900px) {
          .notifications-settings-grid { grid-template-columns: minmax(0, 1fr); }
        }
      `}</style>
    </div>
  )
}
